/**
 * React Query hooks for Workspace Admin API (Q-1)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "../lib/api";
import type { InviteMemberRequest, UpdateMemberRoleRequest } from "../types/api";
import { WORKSPACE_ID_REGEX } from "../types/api";

const WS_KEYS = {
  all: ["workspaces"] as const,
  list: () => [...WS_KEYS.all, "list"] as const,
  visible: () => [...WS_KEYS.all, "visible"] as const,
  primary: () => [...WS_KEYS.all, "primary"] as const,
  members: (workspaceId: string) => [...WS_KEYS.all, "members", workspaceId] as const,
  stats: (period: string) => ["admin", "stats", period] as const,
};

export function useWorkspaces() {
  return useQuery({
    queryKey: WS_KEYS.list(),
    queryFn: () => getApiClient().listWorkspaces(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useVisibleWorkspaces() {
  return useQuery({
    queryKey: WS_KEYS.visible(),
    queryFn: () => getApiClient().listVisibleWorkspaces(),
    staleTime: 1000 * 60 * 2,
  });
}

export function usePrimaryWorkspace() {
  return useQuery({
    queryKey: WS_KEYS.primary(),
    queryFn: () => getApiClient().getPrimaryWorkspace(),
    staleTime: 1000 * 60 * 2,
  });
}

export function useResolvedWorkspace(preferredWorkspaceId?: string) {
  const visibleQuery = useVisibleWorkspaces();
  const preferred = preferredWorkspaceId?.trim() ?? "";
  const hasPreferred = preferred.length > 0;
  const preferredIsValid = !hasPreferred || WORKSPACE_ID_REGEX.test(preferred);
  const workspaces = visibleQuery.data?.workspaces ?? [];
  const primaryWorkspaceId = visibleQuery.data?.primaryWorkspaceId ?? null;

  const resolvedWorkspace =
    (hasPreferred && preferredIsValid
      ? workspaces.find((workspace) => workspace.id === preferred)
      : undefined) ??
    (primaryWorkspaceId
      ? workspaces.find((workspace) => workspace.id === primaryWorkspaceId)
      : undefined) ??
    workspaces[0] ??
    null;

  return {
    ...visibleQuery,
    workspaces,
    preferredWorkspaceId: preferred,
    preferredWorkspaceIsValid: preferredIsValid,
    preferredWorkspaceMatched: hasPreferred && preferredIsValid
      ? workspaces.some((workspace) => workspace.id === preferred)
      : false,
    workspace: resolvedWorkspace,
    workspaceId: resolvedWorkspace?.id ?? "",
  };
}

export function useWorkspaceMembers(workspaceId: string) {
  return useQuery({
    queryKey: WS_KEYS.members(workspaceId),
    queryFn: () => getApiClient().getWorkspaceMembers(workspaceId),
    staleTime: 1000 * 60 * 2,
    enabled: workspaceId.length > 0,
  });
}

export function useInviteMember(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: InviteMemberRequest) =>
      getApiClient().inviteMember(workspaceId, req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WS_KEYS.members(workspaceId) });
    },
  });
}

export function useUpdateMemberRole(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, role }: UpdateMemberRoleRequest & { memberId: string }) =>
      getApiClient().updateMemberRole(workspaceId, memberId, { role }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WS_KEYS.members(workspaceId) });
    },
  });
}

export function useRemoveMember(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      getApiClient().removeMember(workspaceId, memberId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WS_KEYS.members(workspaceId) });
    },
  });
}

export function useOverallStats(period: 'day' | 'week' | 'month' = 'week') {
  return useQuery({
    queryKey: WS_KEYS.stats(period),
    queryFn: () => getApiClient().getOverallStats(period),
    staleTime: 1000 * 60 * 5,
  });
}
